import type { PlatformKind } from './ports/types'

export interface DetectPlatformOptions {
  /** Runtime flag from `NUXT_PUBLIC_MINIAPP_DEV` (truthy when `'1'` / `true`). */
  miniappDev: boolean
  /** `import.meta.dev` — production builds never activate the dev adapter. */
  isDevBuild: boolean
  /**
   * Primary VK probe. Injected so `detectPlatform` does not depend on
   * `@vkontakte/vk-bridge` (DIP). Callers typically pass `bridge.isEmbedded`.
   */
  isVkEmbedded: () => boolean
  /**
   * Optional secondary URL probe. Default is unused for kind selection:
   * bare (or even multi-param) `vk_*` query in a plain browser must not
   * select `vk` — that false-positive runs `VkPlatformAdapter.init` and can
   * hang on `bridge.send` outside the host.
   */
  hasVkUrlLaunch?: () => boolean
}

function hasTelegramLaunch(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  const webApp = window.Telegram?.WebApp
  if (!webApp) {
    return false
  }
  // Script may exist outside Telegram; only treat as TG when launch data is present.
  return Boolean(webApp.initData)
}

/**
 * Weaker URL heuristic kept as an injectable seam for tests.
 * Not used by default for kind selection (see `detectPlatform`).
 * Bare `vk_user_id` alone is never enough; requires another `vk_*` param.
 */
export function hasVkUrlLaunchParams(
  search: string = typeof window !== 'undefined' ? window.location.search || '' : '',
): boolean {
  if (!/(?:^|[?&])vk_user_id=/.test(search)) {
    return false
  }
  return /(?:^|[?&])vk_(?!user_id=)[a-z0-9_]+=/i.test(search)
}

function hasVkLaunch(
  isVkEmbedded: () => boolean,
  hasVkUrlLaunch: () => boolean,
): boolean {
  try {
    if (isVkEmbedded()) {
      return true
    }
  }
  catch {
    // Embed probe failed — fall through to optional secondary.
  }
  return hasVkUrlLaunch()
}

/**
 * Detect host Mini App environment.
 * Priority: real Telegram → real VK → explicit local/e2e dev → none (noop).
 * Simultaneous `MINIAPP_DEV` + Telegram prefers Telegram (real platform wins).
 *
 * VK: prefer injected `isVkEmbedded`. URL `vk_*` params are weaker/secondary
 * and **off by default** so plain browsers never false-positive as `vk`.
 */
export function detectPlatform(options: DetectPlatformOptions): PlatformKind {
  if (hasTelegramLaunch()) {
    return 'telegram'
  }
  if (
    hasVkLaunch(
      options.isVkEmbedded,
      // Default: do not promote URL params to kind=vk outside the embed.
      options.hasVkUrlLaunch ?? (() => false),
    )
  ) {
    return 'vk'
  }
  if (options.isDevBuild && options.miniappDev) {
    return 'dev'
  }
  return 'none'
}

export function isTruthyPublicFlag(value: unknown): boolean {
  if (value === true || value === 1) {
    return true
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    return normalized === '1' || normalized === 'true' || normalized === 'yes'
  }
  return false
}
